/**
 * Phase 1.7 — generic edit-entity dialog.
 *
 * Mirror of AddEntityDialog but for PATCH. Reuses the same FieldDef shape
 * so a v2 page can typically declare its add fields once and pass the
 * same array (filtered to editable fields) here.
 *
 * Open/close is controlled. The dialog hydrates form state from
 * `initialValues` whenever it opens, so the same instance can be reused
 * across rows.
 *
 * Submit semantics: only fields whose value changed from initial are
 * sent to PATCH. This matches the worker's "no fields to update" 400
 * guard and keeps the wire shape minimal.
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { authFetch } from '@/lib/auth';
import { toast } from 'sonner';
import type { FieldDef } from './AddEntityDialog';

export interface EditEntityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  submitLabel?: string;
  /** PATCH target. The dialog appends `/${entityId}` automatically. */
  endpoint: string;
  entityId: string;
  fields: FieldDef[];
  /** Initial values keyed by field name. Missing keys default to ''. */
  initialValues: Record<string, string>;
  /** Optional transform applied to the diff payload before PATCH. */
  transform?: (values: Record<string, string>) => Record<string, unknown>;
  onSaved?: () => void;
}

export function EditEntityDialog({
  open,
  onOpenChange,
  title,
  submitLabel,
  endpoint,
  entityId,
  fields,
  initialValues,
  transform,
  onSaved,
}: EditEntityDialogProps): JSX.Element {
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Re-hydrate when the dialog opens. We deliberately don't depend on
  // `initialValues` or `fields` because those are typically fresh object
  // literals on every parent render — depending on them would clobber
  // the user's in-progress edits with the original values on every key
  // press elsewhere on the page. The parent is expected to remount this
  // component when the target row changes (`{target ? <Edit…/> : null}`),
  // which gives us the open false→true transition we need to re-hydrate.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    const fresh: Record<string, string> = {};
    for (const f of fields) {
      const v = initialValues[f.name];
      fresh[f.name] = v ?? '';
    }
    setValues(fresh);
  }, [open]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    for (const f of fields) {
      if (f.required && !values[f.name]?.trim()) {
        toast.error(`${f.label} is required`);
        return;
      }
    }

    // Build the diff. Treat fields whose canonical value matches the
    // initial value as untouched (avoids PATCH 400 "No fields to update"
    // when nothing changed and avoids stomping null values with "").
    const diff: Record<string, string> = {};
    for (const f of fields) {
      const next = (values[f.name] ?? '').trim();
      const prev = (initialValues[f.name] ?? '').trim();
      if (next !== prev) diff[f.name] = next;
    }
    if (Object.keys(diff).length === 0) {
      onOpenChange(false);
      return;
    }

    setSubmitting(true);
    try {
      const body = transform ? transform(diff) : diff;
      await authFetch(`${endpoint}/${entityId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      toast.success('Saved');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error((err as Error).message ?? 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          {renderFields(fields, values, setValues)}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
          >
            {submitting ? 'Saving…' : (submitLabel ?? 'Save')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function renderFields(
  fields: FieldDef[],
  values: Record<string, string>,
  setValues: React.Dispatch<React.SetStateAction<Record<string, string>>>,
): JSX.Element[] {
  const out: JSX.Element[] = [];
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i];
    if (f.half && fields[i + 1]?.half) {
      out.push(
        <div key={`${f.name}-row`} className="grid grid-cols-2 gap-3">
          {renderField(f, values, setValues)}
          {renderField(fields[i + 1], values, setValues)}
        </div>,
      );
      i += 1;
    } else {
      out.push(renderField(f, values, setValues));
    }
  }
  return out;
}

function renderField(
  f: FieldDef,
  values: Record<string, string>,
  setValues: React.Dispatch<React.SetStateAction<Record<string, string>>>,
): JSX.Element {
  const setVal = (next: string): void => setValues((v) => ({ ...v, [f.name]: next }));
  if (f.type === 'textarea') {
    return (
      <div key={f.name} className="space-y-2">
        <Label>{f.label}{f.required ? ' *' : ''}</Label>
        <Textarea
          rows={3}
          value={values[f.name] ?? ''}
          onChange={(e) => setVal(e.target.value)}
          placeholder={f.placeholder}
        />
      </div>
    );
  }
  if (f.type === 'select') {
    return (
      <div key={f.name} className="space-y-2">
        <Label>{f.label}{f.required ? ' *' : ''}</Label>
        <Select value={values[f.name] ?? ''} onValueChange={setVal}>
          <SelectTrigger>
            <SelectValue placeholder={f.placeholder ?? 'Select…'} />
          </SelectTrigger>
          <SelectContent>
            {(f.options ?? []).map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  return (
    <div key={f.name} className="space-y-2">
      <Label>{f.label}{f.required ? ' *' : ''}</Label>
      <Input
        type={f.type ?? 'text'}
        value={values[f.name] ?? ''}
        onChange={(e) => setVal(e.target.value)}
        placeholder={f.placeholder}
      />
    </div>
  );
}
